/**
 * useVisualPackage — fetches and parses a .visual zip archive on demand.
 *
 * .visual files are zip archives served to the frontend as-is via a
 * presigned S3 URL. This hook fetches the zip, extracts its contents with
 * JSZip, normalises metadata.json into VisualMetadata, and creates blob URLs
 * for the internal files (thumbnail, SPA index.html, print PDF).
 *
 * Results are cached by artifactId for the lifetime of the browser tab to
 * avoid repeated fetches. Blob URLs in the cache are NOT revoked on
 * component unmount — they are intentionally long-lived.
 *
 * Usage:
 *   const { data, isLoading, error } = useVisualPackage(artifactId, presignedUrl)
 *   // data is ParsedVisual | null
 */
import { useState, useEffect } from 'react'
import JSZip from 'jszip'
import type { ParsedVisual, VisualMetadata, VisualUrls } from '../api/types/visual-metadata'
import { mountVisual, type VisualFile } from '../lib/visualServiceWorker'

// ─── Module-level cache ───────────────────────────────────────────────────────
// Keyed by artifactId. Stores the fully-resolved ParsedVisual (never a partial).
const _cache = new Map<string, ParsedVisual>()

// ─── Raw metadata.json shape (before normalisation) ──────────────────────────

interface RawMetadata {
  version?: string
  type?: string
  jobNumber?: string
  classification?: string
  title?: string
  caption?: string
  target?: string
  targetWidth?: string | null
  targetHeight?: string | null
  inlineImage?: string
  inlineWidth?: string
  inlineHeight?: string | null
  aspectRatio?: number | string | null
  autoPlay?: boolean | string
  float?: string
  tags?: string[]
  specialHandling?: string
  file?: {
    print?: {
      noSourced?: string
    }
  }
}

// ─── Normalisation helpers ────────────────────────────────────────────────────

function normaliseString(v: string | null | undefined, fallback: string): string {
  if (v === null || v === undefined || v === '') return fallback
  return v
}

function normaliseNullableString(v: string | null | undefined): string | null {
  if (v === null || v === undefined || v === '') return null
  return v
}

function normaliseBoolean(v: boolean | string | null | undefined): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return v.toLowerCase() === 'true'
  return false
}

function normaliseFloat(v: string | null | undefined): string | null {
  if (!v || v === '') return null
  return v
}

function normaliseAspectRatio(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'string' ? parseFloat(v) : v
  return isFinite(n) ? n : null
}

function normaliseFloat2(v: string | null | undefined): 'left' | 'right' | 'none' {
  if (v === 'left' || v === 'right') return v
  return 'none'
}

/** Parse a raw metadata.json object into our normalised VisualMetadata shape. */
function normaliseMetadata(raw: RawMetadata): VisualMetadata {
  const target = raw.target === 'popup' ? 'popup' : 'inline'
  const type   = raw.type === 'interactive' ? 'interactive' : 'static'

  return {
    version:        normaliseString(raw.version, '1.0.0'),
    type,
    jobNumber:      normaliseString(raw.jobNumber, 'unknown'),
    classification: normaliseString(raw.classification, 'UNCLASSIFIED'),
    target,
    targetWidth:    target === 'popup' ? normaliseNullableString(raw.targetWidth) : null,
    targetHeight:   target === 'popup' ? normaliseNullableString(raw.targetHeight) : null,
    inlineImage:    normaliseString(raw.inlineImage, 'placeholder.jpg'),
    inlineWidth:    normaliseString(raw.inlineWidth, '100%'),
    inlineHeight:   normaliseFloat(raw.inlineHeight),
    aspectRatio:    normaliseAspectRatio(raw.aspectRatio),
    autoPlay:       normaliseBoolean(raw.autoPlay),
    float:          normaliseFloat2(raw.float),
    title:          normaliseString(raw.title, ''),
    caption:        normaliseString(raw.caption, ''),
    printFile:      raw.file?.print?.noSourced ?? null,
    tags:           Array.isArray(raw.tags) ? raw.tags : [],
    _rawVersion:    normaliseString(raw.version, ''),
  }
}

// ─── Blob URL helpers ─────────────────────────────────────────────────────────

async function fileToBlob(zip: JSZip, path: string, mimeType: string): Promise<string | null> {
  const file = zip.file(path)
  if (!file) return null
  const blob = await file.async('blob')
  const typedBlob = new Blob([blob], { type: mimeType })
  return URL.createObjectURL(typedBlob)
}

/**
 * Mount all interactive SPA files into the service worker under
 * /visual-proxy/{artifactId}/ and return that URL as the content URL.
 * The SW rewrites <base href> on the fly so Angular and other SPAs
 * resolve their chunk/asset URLs correctly.
 */
async function buildInteractiveContentUrl(
  zip: JSZip,
  _metadata: VisualMetadata,
  artifactId: string,
): Promise<string | null> {
  if (!zip.file('index.html')) return null

  const jobs: Promise<VisualFile | null>[] = []
  zip.forEach((relativePath, zipEntry) => {
    if (zipEntry.dir) return
    if (relativePath === 'metadata.json') return
    const job = (async (): Promise<VisualFile> => {
      const bytes = await zipEntry.async('uint8array')
      const ext = relativePath.split('.').pop()?.toLowerCase() ?? ''
      const mime = MIME_MAP[ext] ?? 'application/octet-stream'
      return { path: relativePath, bytes, mime }
    })()
    jobs.push(job)
  })

  const results = await Promise.all(jobs)
  const files = results.filter((f): f is VisualFile => f !== null)

  return mountVisual(artifactId, files)
}

const MIME_MAP: Record<string, string> = {
  html: 'text/html',
  js:   'text/javascript',
  css:  'text/css',
  jpg:  'image/jpeg',
  jpeg: 'image/jpeg',
  png:  'image/png',
  gif:  'image/gif',
  svg:  'image/svg+xml',
  woff: 'font/woff',
  woff2:'font/woff2',
  pdf:  'application/pdf',
  json: 'application/json',
}

// ─── Core parse function ──────────────────────────────────────────────────────

async function urlToArrayBuffer(url: string): Promise<ArrayBuffer> {
  // data: URLs can't be fetched directly in all environments (e.g. the mock
  // service stores uploads as data URLs). Decode them without fetch.
  if (url.startsWith('data:')) {
    const comma = url.indexOf(',')
    if (comma === -1) throw new Error('Invalid data URL')
    const base64 = url.slice(comma + 1)
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes.buffer
  }
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch .visual package: ${response.status} ${response.statusText}`)
  }
  return response.arrayBuffer()
}

async function parseVisualPackage(artifactId: string, presignedUrl: string): Promise<ParsedVisual> {
  const arrayBuffer = await urlToArrayBuffer(presignedUrl)
  const zip = await JSZip.loadAsync(arrayBuffer)

  // Parse metadata.json
  const metaFile = zip.file('metadata.json')
  if (!metaFile) throw new Error('.visual archive is missing metadata.json')
  const rawJson = await metaFile.async('string')
  let raw: RawMetadata
  try {
    raw = JSON.parse(rawJson) as RawMetadata
  } catch {
    throw new Error('metadata.json in .visual archive is not valid JSON')
  }
  const metadata = normaliseMetadata(raw)

  // Build blob URLs
  const thumbnailMime = metadata.inlineImage.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg'
  const thumbnailUrl = await fileToBlob(zip, metadata.inlineImage, thumbnailMime)
  if (!thumbnailUrl) {
    throw new Error(`.visual archive is missing the thumbnail file: ${metadata.inlineImage}`)
  }

  let contentUrl: string | null = null
  if (metadata.type === 'interactive') {
    contentUrl = await buildInteractiveContentUrl(zip, metadata, artifactId)
  }

  let printUrl: string | null = null
  if (metadata.printFile && metadata.printFile.toLowerCase().endsWith('.pdf')) {
    printUrl = await fileToBlob(zip, metadata.printFile, 'application/pdf')
  }

  const urls: VisualUrls = { thumbnailUrl, contentUrl, printUrl }
  return { metadata, urls }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type UseVisualPackageResult = {
  data: ParsedVisual | null
  isLoading: boolean
  error: string | null
}

/**
 * Fetch and parse the .visual zip artifact identified by `artifactId`.
 * `presignedUrl` must be the current presigned download URL for the artifact.
 *
 * Returns null data while loading or when artifactId/presignedUrl is absent.
 * Results are cached by artifactId — subsequent calls return the cached value
 * without re-fetching.
 */
export function useVisualPackage(
  artifactId: string | null | undefined,
  presignedUrl: string | null | undefined,
): UseVisualPackageResult {
  const [data, setData]     = useState<ParsedVisual | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError]   = useState<string | null>(null)

  useEffect(() => {
    if (!artifactId || !presignedUrl) return

    // Cache hit
    const cached = _cache.get(artifactId)
    if (cached) {
      setData(cached)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    parseVisualPackage(artifactId, presignedUrl)
      .then((parsed) => {
        if (cancelled) return
        _cache.set(artifactId, parsed)
        setData(parsed)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load visual package')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => { cancelled = true }
  }, [artifactId, presignedUrl])

  return { data, isLoading, error }
}
